import React, { useState } from "react";
import { Mail, Send, CheckCircle2, Loader2, Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { PageHero } from "@/components/Bits";
import { ORG } from "@/config/content";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-sm border border-gold/30 bg-ink px-4 py-4 text-lg text-ivory placeholder:text-ivory2/40 focus:border-gold";

  return (
    <div>
      <Seo title="Contact Us" description="Get in touch with EHSIMFFA — the Emperor Haile Selassie I Memorial Foundation's Friends in America." path="/contact" />
      <PageHero kicker="Get in Touch" title="Contact the Foundation" subtitle="We would be glad to hear from you — whether about our scholarships, our work in Ethiopia, or how you can help." />

      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-28 grid gap-14 lg:grid-cols-[1fr_1.2fr]">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-ivory">Reach us directly</h2>
          <a href={`mailto:${ORG.email}`} data-testid="contact-direct-email" className="mt-6 inline-flex items-center gap-3 text-lg text-gold hover:text-goldhi">
            <Mail className="h-5 w-5" /> {ORG.email}
          </a>
          <p className="mt-6 text-ivory2 leading-relaxed">
            EHSIMFFA is a 501(c)(3) not-for-profit registered in the {ORG.state}. All donations are fully
            tax-deductible.
          </p>
          <div className="mt-8 flex gap-3">
            {[
              { Icon: Facebook, href: ORG.social.facebook, label: "Facebook" },
              { Icon: Instagram, href: ORG.social.instagram, label: "Instagram" },
              { Icon: Linkedin, href: ORG.social.linkedin, label: "LinkedIn" },
              { Icon: Twitter, href: ORG.social.twitter, label: "Twitter" },
            ].map(({ Icon, href, label }) => (
              <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}
                className="flex h-11 w-11 items-center justify-center rounded-sm hairline text-ivory2 hover:text-gold hover:border-gold/50 transition-colors">
                <Icon className="h-5 w-5" />
              </a>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          {sent ? (
            <div className="flex flex-col items-center justify-center rounded-sm border border-gold/30 bg-ink2 p-12 text-center" data-testid="contact-success" role="status">
              <CheckCircle2 className="h-14 w-14 text-gold" />
              <h3 className="mt-6 font-serif text-3xl text-ivory">Thank you.</h3>
              <p className="mt-3 text-ivory2">Your message has been sent. We will be in touch soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5 rounded-sm border border-gold/25 bg-ink2 p-6 sm:p-8" data-testid="contact-form">
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={upd("website")} className="hidden" aria-hidden="true" />
              <div>
                <label htmlFor="c-name" className="mb-2 block text-sm font-semibold uppercase tracking-wider text-ivory2">Name</label>
                <input id="c-name" type="text" required value={form.name} onChange={upd("name")} className={inputCls} data-testid="contact-name" />
              </div>
              <div>
                <label htmlFor="c-email" className="mb-2 block text-sm font-semibold uppercase tracking-wider text-ivory2">Email</label>
                <input id="c-email" type="email" required value={form.email} onChange={upd("email")} className={inputCls} data-testid="contact-email" />
              </div>
              <div>
                <label htmlFor="c-phone" className="mb-2 block text-sm font-semibold uppercase tracking-wider text-ivory2">Phone <span className="normal-case font-normal text-ivory2/50">(optional)</span></label>
                <input id="c-phone" type="tel" value={form.phone} onChange={upd("phone")} className={inputCls} data-testid="contact-phone" />
              </div>
              <div>
                <label htmlFor="c-msg" className="mb-2 block text-sm font-semibold uppercase tracking-wider text-ivory2">Message</label>
                <textarea id="c-msg" required rows={5} value={form.message} onChange={upd("message")} className={inputCls} data-testid="contact-message" />
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit"
                className="flex w-full items-center justify-center gap-3 rounded-sm bg-gold py-4 text-base font-bold uppercase tracking-wider text-ink transition-colors hover:bg-goldhi disabled:opacity-70">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</> : <><Send className="h-5 w-5" /> Send Message</>}
              </button>
            </form>
          )}
        </Reveal>
      </section>
    </div>
  );
}
